import {Company} from '../extensions/company'
import {Sync} from './sync'
import {User} from 'yougile-model'
import {SyncEvents, SyncUpdate, SyncListenerFilter} from './sync-events'
import {globals} from '../extensions/globals-base'
import {DataObjectId} from 'yougile-model'
import {Hub} from 'yougile-model'
import {ChatProxy} from '../extensions/proxies/chat-proxy'
import {TaskChatProxyService} from '../extensions/proxies/task-chat-proxy'
import {SandboxTree} from 'yougile-model'
import {ProjectProxy} from '../extensions/proxies/project-proxy'
import {Project} from '../extensions/project'

export class SyncListeners {
  private static listeners: Map<() => void, number[]> = new Map()

  public static addTaskChatsListener(
    update: () => void,
    user: User,
    cachedChatsGetter: () => ReadonlyArray<ChatProxy>
  ): void {
    const listenerIds = Company.list().map((c: Company) => {
      const box = Sync.get({companyId: c.id})
      return SyncEvents.addListener(c.id, '*', (ups: SyncUpdate[]) => {
        if (globals.getCompanyId() !== c.id) {
          return
        }
        const cachedChats = cachedChatsGetter()
        let ids: DataObjectId[] = []
        ups.forEach((upd: SyncUpdate) => (ids = ids.concat(upd.ids)))
        const uniq: Set<DataObjectId> = new Set(ids)
        let shouldUpdate = false
        uniq.forEach((id: DataObjectId) => {
          if (shouldUpdate) {
            return
          }
          const obj = box.get(id)
          if (obj instanceof Hub && obj.chat.length > 0) {
            const ix = cachedChats.findIndex(({chatId}) => chatId === id)
            if (ix === -1) {
              shouldUpdate = true
            } else {
              const {
                chatName,
                newMessagesAmount,
                lastMessageTimestamp,
                lastMessageText
              } = cachedChats[ix]

              const newChat = TaskChatProxyService.create(user, obj)

              if (
                chatName !== obj.title ||
                newMessagesAmount !== newChat.newMessagesAmount ||
                lastMessageTimestamp !== newChat.lastMessageTimestamp ||
                lastMessageText !== newChat.lastMessageText
              ) {
                shouldUpdate = true
              }
            }
          }
        })

        if (shouldUpdate) {
          update()
        }
      })
    })

    SyncListeners.add(update, listenerIds)
  }

  public static addCompaniesListener(update: () => void): void {
    const listenerIds = Sync.listSandboxes().map(({companyId}: SandboxTree) =>
      SyncEvents.addListener(companyId, companyId, update)
    )

    const {companyId: profileId} = Sync.getProfile()
    const {id} = Sync.getCurrentUser()
    const userListenerId = SyncEvents.addListener(profileId, id, update)

    SyncListeners.add(update, [...listenerIds, userListenerId])
  }

  public static addProjectsListener(
    update: () => void,
    cachedProjectsGetter: () => ReadonlyArray<ProjectProxy>
  ): void {
    const listenerIds = Company.list().map((c: Company) => {
      return SyncEvents.addListener(c.id, '*', () => {
        const projects = Project.listProjects(c.id, 'all').sort(
          Project.projectOrderComparer
        )

        const cachedProjects = cachedProjectsGetter()
        const items = cachedProjects.filter(
          ({parentCompanyId}) => c.id === parentCompanyId
        )

        const projectIds = projects.map(({id}) => id)
        const cachedProjectIds = items.map(({projectId}) => projectId)

        const isDifferent =
          projectIds.length !== cachedProjectIds.length ||
          cachedProjectIds.some((id, idx) => projectIds[idx] !== id)

        const shouldUpdate =
          isDifferent ||
          items.reduce(
            (acc: boolean, {projectName, projectId}: ProjectProxy) =>
              acc ||
              projects.find(({id}) => id === projectId).title !== projectName,
            false
          )

        if (shouldUpdate) {
          update()
        }
      })
    })

    SyncListeners.add(update, listenerIds)
  }

  public static addListener(
    update: () => void,
    companyId: DataObjectId,
    filter: SyncListenerFilter
  ): void {
    SyncListeners.add(update, [
      SyncEvents.addListener(companyId, filter, () => update())
    ])
  }

  public static removeListener(fn: () => void): void {
    SyncListeners.remove(fn)
  }

  private static add(fn: () => void, listenerIds: number[]): void {
    const {listeners} = SyncListeners

    listeners.set(fn, [...listenerIds, ...(listeners.get(fn) || [])])
  }

  private static remove(fn: () => void): void {
    const {listeners} = SyncListeners

    if (listeners.has(fn)) {
      listeners.get(fn).forEach(SyncEvents.removeListener)
      listeners.delete(fn)
    }
  }
}
